import React, { useMemo, useState, useRef, useCallback } from 'react';
import { InjuryRiskTimelinePoint } from '@/types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CompactTimelineProps {
  data: InjuryRiskTimelinePoint[];
  isDark: boolean;
}

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

function greenFor(dark: boolean): string {
  return dark ? '#4ade80' : '#16a34a';
}

function neutralFor(dark: boolean): string {
  return dark ? '#ffffff' : '#1a1a1a';
}

function getRiskBarColor(riskScore: number, dark: boolean): string {
  if (riskScore < 0.2) return greenFor(dark);
  if (riskScore > 0.4) return '#ef4444';
  return neutralFor(dark);
}

function getRiskBarOpacity(riskScore: number, dark: boolean): number {
  const boost = dark ? 0 : 0.15;
  // Continuous opacity ramp: 0.12 at 0 → 0.18 at 0.2 → 0.22 at 0.4 → 0.65 at 1.0
  if (riskScore < 0.2) return 0.12 + riskScore * 0.3 + boost;
  if (riskScore > 0.4) return Math.min(0.22 + (riskScore - 0.4) * 0.7, 0.65) + boost;
  return 0.18 + (riskScore - 0.2) * 0.2 + boost; // starts at 0.18 (matches green endpoint)
}

function getInvertedBarColor(value: number, baseline: number, dark: boolean): string {
  if (baseline === 0) return neutralFor(dark);
  const ratio = value / baseline;
  if (ratio > 1.05) return greenFor(dark);
  if (ratio < 0.85) return '#ef4444';
  return neutralFor(dark);
}

function getInvertedBarOpacity(value: number, baseline: number, dark: boolean): number {
  const boost = dark ? 0 : 0.15;
  if (baseline === 0) return 0.1 + boost;
  const ratio = value / baseline;
  if (ratio > 1.05) return 0.15 + (ratio - 1) * 0.3 + boost;
  if (ratio < 0.85) return 0.15 + (1 - ratio) * 0.5 + boost;
  return 0.1 + boost;
}

function getAcwrBarColor(value: number, threshold: number, dark: boolean): string {
  if (value < threshold * 0.85) return greenFor(dark);
  if (value < threshold) return neutralFor(dark);
  return '#ef4444';
}

function getAcwrBarOpacity(value: number, threshold: number, dark: boolean): number {
  const boost = dark ? 0 : 0.15;
  if (value < threshold * 0.85) return 0.12 + boost;
  if (value < threshold) return 0.08 + boost;
  const excess = (value - threshold) / threshold;
  return 0.2 + Math.min(excess * 0.8, 0.5) + boost;
}

// ---------------------------------------------------------------------------
// Compounding annotation finder
// ---------------------------------------------------------------------------

function findCompoundingStart(data: InjuryRiskTimelinePoint[]): number | null {
  // Find the first index where 2+ consecutive increases (3 ascending points) begin with total delta > 0.05
  for (let i = 0; i < data.length - 2; i++) {
    let streak = 1;
    let delta = 0;
    for (let j = i + 1; j < data.length && data[j].risk_score > data[j - 1].risk_score; j++) {
      streak++;
      delta = data[j].risk_score - data[i].risk_score;
    }
    if (streak >= 3 && delta > 0.05) {
      return i;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Row types
// ---------------------------------------------------------------------------

type RowConfig = {
  label: string;
  key: keyof InjuryRiskTimelinePoint;
  maxValue: number;
  type: 'risk' | 'acwr' | 'inverted';
  threshold?: number;
};

const ROWS: RowConfig[] = [
  { label: 'Risk',  key: 'risk_score',      maxValue: 1,   type: 'risk'     },
  { label: 'ACWR',  key: 'acwr_composite',   maxValue: 2,   type: 'acwr',   threshold: 1.3 },
  { label: 'HRV',   key: 'hrv_rmssd',        maxValue: 120, type: 'inverted' },
  { label: 'Sleep', key: 'sleep_hours',      maxValue: 10,  type: 'inverted' },
];

// ---------------------------------------------------------------------------
// Individual bar component
// ---------------------------------------------------------------------------

interface BarProps {
  value: number | null;
  maxValue: number;
  rowType: 'risk' | 'acwr' | 'inverted';
  baseline: number;
  threshold?: number;
  dark: boolean;
  barIdx: number;
  hoveredIdx: number | null;
}

const Bar: React.FC<BarProps> = ({ value, maxValue, rowType, baseline, threshold, dark, barIdx, hoveredIdx }) => {
  const isHovered = barIdx === hoveredIdx;

  if (value === null || value === undefined) {
    return (
      <div
        style={{
          flex: '1 0 0',
          height: '100%',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <div
          style={{
            width: '100%',
            height: '2%',
            backgroundColor: 'var(--text-muted)',
            opacity: isHovered ? 0.6 : 0.4,
          }}
        />
      </div>
    );
  }

  const heightPct = Math.min(Math.max((value / maxValue) * 100, 2), 100);

  let color: string;
  let opacity: number;

  if (rowType === 'risk') {
    color = getRiskBarColor(value, dark);
    opacity = getRiskBarOpacity(value, dark);
  } else if (rowType === 'acwr') {
    const thresh = threshold ?? 1.3;
    color = getAcwrBarColor(value, thresh, dark);
    opacity = getAcwrBarOpacity(value, thresh, dark);
  } else {
    // inverted
    color = getInvertedBarColor(value, baseline, dark);
    opacity = getInvertedBarOpacity(value, baseline, dark);
  }

  const finalOpacity = isHovered ? Math.min(opacity + 0.2, 1) : opacity;

  return (
    <div
      style={{
        flex: '1 0 0',
        height: '100%',
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      <div
        style={{
          width: '100%',
          height: `${heightPct}%`,
          backgroundColor: color,
          opacity: finalOpacity,
        }}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Timeline row component
// ---------------------------------------------------------------------------

interface TimelineRowProps {
  label: string;
  data: InjuryRiskTimelinePoint[];
  rowConfig: RowConfig;
  baseline: number;
  compoundingIdx: number | null;
  barCount: number;
  isDark: boolean;
  hoveredIdx: number | null;
  onHoverIdx?: (idx: number | null, xPx?: number) => void;
}

const TimelineRow: React.FC<TimelineRowProps> = ({
  label,
  data,
  rowConfig,
  baseline,
  compoundingIdx,
  barCount,
  isDark,
  hoveredIdx,
  onHoverIdx,
}) => {
  const { key, maxValue, type, threshold } = rowConfig;

  // For threshold/baseline line, compute the height % in the bar area
  let annotationHeightPct: number | null = null;
  let annotationLabel: string | null = null;

  if (type === 'acwr' && threshold !== undefined) {
    annotationHeightPct = (threshold / maxValue) * 100;
    annotationLabel = '1.3';
  } else if (type === 'inverted' && baseline > 0) {
    annotationHeightPct = (baseline / maxValue) * 100;
    annotationLabel = 'avg';
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: '32px', marginBottom: '3px' }}>
      {/* Label */}
      <div
        style={{
          width: '44px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'monospace',
          fontSize: '9px',
          color: 'var(--text-secondary)',
          paddingRight: '4px',
        }}
      >
        {label}
      </div>

      {/* Bar container */}
      <div
        style={{
          flex: 1,
          height: '32px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: '1px',
          position: 'relative',
          cursor: 'crosshair',
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const idx = Math.floor((x / rect.width) * barCount);
          onHoverIdx?.(Math.max(0, Math.min(idx, barCount - 1)), x);
        }}
        onMouseLeave={() => onHoverIdx?.(null)}
      >
        {data.map((point, i) => (
          <Bar
            key={i}
            value={point[key] as number | null}
            maxValue={maxValue}
            rowType={type}
            baseline={baseline}
            threshold={threshold}
            dark={isDark}
            barIdx={i}
            hoveredIdx={hoveredIdx}
          />
        ))}

        {/* Threshold / baseline dashed line + label */}
        {annotationHeightPct !== null && annotationHeightPct <= 100 && (
          <div
            style={{
              position: 'absolute',
              bottom: `${annotationHeightPct}%`,
              left: 0,
              right: 0,
              height: 0,
              borderTop: '1px dotted',
              borderColor: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
              pointerEvents: 'none',
            }}
          >
            {annotationLabel && (
              <span
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '-10px',
                  fontFamily: 'monospace',
                  fontSize: '8px',
                  color: isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  background: 'var(--bg-card)',
                  padding: '0 2px',
                }}
              >
                {annotationLabel}
              </span>
            )}
          </div>
        )}

        {/* Compounding annotation (Risk row only) */}
        {type === 'risk' && compoundingIdx !== null && barCount > 0 && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${(compoundingIdx / barCount) * 100}%`,
                width: '1px',
                borderLeft: '1px dashed var(--text-secondary)',
                pointerEvents: 'none',
              }}
            />
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: `calc(${(compoundingIdx / barCount) * 100}% + 3px)`,
                fontFamily: 'monospace',
                fontSize: '7px',
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}
            >
              compounding
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tooltip value formatting
// ---------------------------------------------------------------------------

function formatRowValue(value: number, row: RowConfig): string {
  switch (row.key) {
    case 'risk_score':
      return `${Math.round(value * 100)}/100`;
    case 'acwr_composite':
      return value.toFixed(2);
    case 'hrv_rmssd':
      return `${value.toFixed(0)}ms`;
    case 'sleep_hours':
      return `${value.toFixed(1)}hrs`;
    default:
      return String(value);
  }
}

function formatTooltipDate(dateStr: string): string {
  const dt = new Date(dateStr);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const CompactTimeline: React.FC<CompactTimelineProps> = ({ data, isDark }) => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [hoveredX, setHoveredX] = useState<number | null>(null);
  const rowsContainerRef = useRef<HTMLDivElement>(null);

  const handleHoverIdx = useCallback((idx: number | null, xPx?: number) => {
    setHoveredIdx(idx);
    setHoveredX(xPx ?? null);
  }, []);

  // Compute baselines for inverted rows
  const hrvBaseline = useMemo(() => {
    const vals = data.map(d => d.hrv_rmssd).filter((v): v is number => v !== null && v > 0);
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  }, [data]);

  const sleepBaseline = useMemo(() => {
    const vals = data.map(d => d.sleep_hours).filter((v): v is number => v !== null && v > 0);
    return vals.length > 0 ? vals.reduce((s, v) => s + v, 0) / vals.length : 0;
  }, [data]);

  const compoundingIdx = useMemo(() => findCompoundingStart(data), [data]);

  // X-axis dates — ~5 evenly spaced
  const xAxisDates = useMemo(() => {
    if (data.length === 0) return [];
    const step = Math.max(1, Math.floor((data.length - 1) / 4));
    const indices: number[] = [];
    for (let i = 0; i < data.length; i += step) {
      indices.push(i);
    }
    // Ensure last date is included
    if (indices[indices.length - 1] !== data.length - 1) {
      indices.push(data.length - 1);
    }
    return indices.map(i => {
      const dt = new Date(data[i].date);
      return { idx: i, label: `${dt.getMonth() + 1}/${dt.getDate()}` };
    });
  }, [data]);

  const getBaseline = (rowType: RowConfig['type'], key: keyof InjuryRiskTimelinePoint): number => {
    if (key === 'hrv_rmssd') return hrvBaseline;
    if (key === 'sleep_hours') return sleepBaseline;
    return 0;
  };

  if (!data || data.length === 0) return null;

  // Clamp tooltip near edges to prevent overflow
  const isNearRight = hoveredIdx !== null && hoveredIdx > data.length * 0.8;
  const isNearLeft = hoveredIdx !== null && hoveredIdx < data.length * 0.2;

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Section label */}
      <div
        style={{
          fontSize: '10px',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
          letterSpacing: '0.1em',
          fontWeight: 600,
          marginBottom: '6px',
        }}
      >
        28-Day Overview
      </div>

      {/* Container */}
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--card-border)',
          padding: '20px',
          marginBottom: '0',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginBottom: '12px',
          }}
        >
          <span
            style={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--text-primary)',
            }}
          >
            Aligned Signal Trends
          </span>
        </div>

        {/* Rows wrapper — positioned for tooltip + vertical hover line */}
        <div ref={rowsContainerRef} style={{ position: 'relative' }}>
          {/* Tooltip */}
          {hoveredIdx !== null && hoveredX !== null && data[hoveredIdx] && (
            <div
              style={{
                position: 'absolute',
                left: `${44 + hoveredX}px`,
                top: '-8px',
                transform: `translateX(${isNearRight ? '-90%' : isNearLeft ? '-10%' : '-50%'}) translateY(-100%)`,
                background: 'var(--bg-card)',
                border: '1px solid var(--card-border)',
                padding: '8px 12px',
                fontSize: '11px',
                whiteSpace: 'nowrap',
                zIndex: 10,
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                borderRadius: 0,
              }}
            >
              <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                {formatTooltipDate(data[hoveredIdx].date)}
              </div>
              {ROWS.map(row => {
                const v = data[hoveredIdx][row.key] as number | null;
                return v != null ? (
                  <div
                    key={row.key as string}
                    style={{
                      color: 'var(--text-secondary)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                    }}
                  >
                    <span>{row.label}:</span>
                    <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>
                      {formatRowValue(v, row)}
                    </span>
                  </div>
                ) : null;
              })}
            </div>
          )}

          {/* Vertical hover line */}
          {hoveredIdx !== null && hoveredX !== null && (
            <div
              style={{
                position: 'absolute',
                left: `${44 + hoveredX}px`,
                top: 0,
                bottom: 0,
                width: '1px',
                borderLeft: '1px dashed var(--text-muted)',
                opacity: 0.5,
                pointerEvents: 'none',
                zIndex: 5,
              }}
            />
          )}

          {/* Rows — hide rows with fewer than 3 non-null values */}
          {ROWS.filter(row => {
            const nonNull = data.filter(d => (d[row.key] as number | null) != null).length;
            return nonNull >= 3;
          }).map((row) => (
            <TimelineRow
              key={row.key as string}
              label={row.label}
              data={data}
              rowConfig={row}
              baseline={getBaseline(row.type, row.key)}
              compoundingIdx={row.type === 'risk' ? compoundingIdx : null}
              barCount={data.length}
              isDark={isDark}
              hoveredIdx={hoveredIdx}
              onHoverIdx={handleHoverIdx}
            />
          ))}
        </div>

        {/* X-axis */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: '4px',
            paddingLeft: '44px',
            position: 'relative',
          }}
        >
          <div style={{ flex: 1, position: 'relative', height: '12px' }}>
            {xAxisDates.map(({ idx, label }) => (
              <span
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(idx / (data.length - 1)) * 100}%`,
                  transform: 'translateX(-50%)',
                  fontFamily: 'monospace',
                  fontSize: '8px',
                  color: 'var(--text-muted)',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompactTimeline;
